import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import ImageLayer from 'ol/layer/Image';
import { ImageWMS, TileWMS } from 'ol/source';
import axios from 'axios';
import { ImageTile } from 'ol';
import TileLayer from 'ol/layer/Tile';
import React from 'react';
import { IconButton } from '@mui/material';
import { Layer } from 'ol/layer';

import { useSessionStore } from 'session/useSessionStore';
import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { SeismicMiniMapContent } from './SeismicMiniMapContent';
import { ILineString } from 'features/seismic/models/interfaces/ILineString';
import { GeopostWMSSource } from 'features/seismic/models/classes/layers/GeopostWMSSource';
import { useAPI } from 'hooks/useAPI';
import { IMiniMap } from 'features/seismic/models/interfaces/IMiniMap';
import { Position } from 'components/geopostPopup/models/enums/Position';
import { useFeatureFlags } from 'features/seismic/hooks/useFeatureFlags';
import { ApiType } from 'models/enums/ApiType';
import { Reset } from 'components/icons/components';
import { MinimapBaseLayer } from 'features/seismic-3d/models/enums/MinimapBaseLayer';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';

export function SeismicMiniMapPopup ({ opened, setOpened, dataGeometryLine, hasGeometryError: hasGeometryError }:Props){
    const { t } = useTranslation();
    const allLayers = useRef<Layer[]>([]);
    const {tenantConfig, jwtToken} = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
        jwtToken: state.jwtToken
    }));
    const [refreshMiniMap, setRefreshMiniMap] = useState<number>(0);

    const { featureFlags } = useFeatureFlags();

    const { current: urlParams } = useRef(getParamsFromUrl());

    const { execute } = useAPI<IMiniMap[]>('/SeismicLayers/GetAllLayers');

    const [ visibleLayers, setVisibleLayers ] = useState([...allLayers.current]);

    const miniMapSrid = useMemo(() => tenantConfig?.minimapBaseLayer === MinimapBaseLayer.BING ? 3857 : tenantConfig?.miniMapSrid, [tenantConfig]);

    const refresh = () => {
        setRefreshMiniMap(refreshMiniMap + 1);
    };

    const buildIconHeaders = () => {
        if (localStorage['lastAutoCreatedPermalink'] !== '' && localStorage['lastAutoCreatedPermalink'] !== undefined && localStorage['lastAutoCreatedPermalink'] !== null) {
            return ([
                <IconButton color='inherit' size='small' onClick={refresh} title={t('refresh')}>
                    <Reset />
                </IconButton >
            ]);
        }

        return ([<></>]);
    };

    useEffect(() => {
        const fetch = async () => {
            let dataMap = await execute({
                apiType: ApiType.LayersApi,
                params: {
                    permalinkToken:  urlParams.permalinkToken ?? localStorage['lastAutoCreatedPermalink'] ?? ''
                }
            });

            if (dataMap) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const visible: Layer[] = [];

                for (let i = 0; i < dataMap.length; i++) {
                    const layerData = dataMap[i];
                    const key = `${layerData.WorkspaceName}:${layerData.LayerName}`;
                    let layer: Layer|undefined;

                    layer = allLayers.current.find(l => l.get('layerKey') === key);
                    if (!!layer) {
                        visible.push(layer);
                        continue;
                    }

                    if (layerData.FromGeopost) {
                        const source = new GeopostWMSSource({
                            url: dataMap[i].HostUrl.replace('http://', '').replace('https://', '') + '/geoserver/wms',
                            params: { LAYERS: dataMap[i].WorkspaceName + ':' + dataMap[i].LayerName },
                        }, jwtToken, tenantConfig?.endpoints.miniMapProxy ?? '');
                        layer = new TileLayer({ source });
                    } else {
                        const source = new ImageWMS({
                            url: layerData.ProxyUrl ?? layerData.HostUrl,
                            params: { LAYERS: `${!!layerData.WorkspaceName ? layerData.WorkspaceName + ':' : ''}${layerData.LayerName}` },
                            imageLoadFunction: (img, src) => {
                                let jwt = jwtToken;
                                if (window.location.origin.includes('localhost') || window.location.origin.includes('gcp-gpost')) {
                                    jwt = tenantConfig?.forceApiTgsToken ?? '';
                                }
                                axios.get(src, { headers: { Authorization: 'Bearer ' + jwt }, responseType: 'blob' }).then(response => {
                                    const src = URL.createObjectURL(response.data);
                                    (img.getImage() as HTMLImageElement).src = src;
                                });
                            }
                        });
                        layer = new ImageLayer({
                            source,
                        });
                    }

                    layer.set('layerKey', key);
                    visible.push(layer);
                    allLayers.current = [...allLayers.current, layer];
                }

                setVisibleLayers(visible);
            };
        };

        try {
            if (featureFlags?.seismic2D.MinimapProjections) {
                fetch();
            }
        }
        catch (e) {
            console.log('Error on get layers to minimap');
        }
    }, [featureFlags?.seismic2D.MinimapProjections, refreshMiniMap]);

    return (
        miniMapSrid
            ?
            <GeopostPopup
                temporaryIgnoreFlexInContainer={false}
                open={opened}
                title={t('miniMap')}
                onChangeOpen={value => setOpened(value)}
                iconHeaders={buildIconHeaders()}
                overlay={false}
                height={211}
                width={398}
                position={Position.topRight}
                customYPosition={55}
                rebuildOnOpen={false}
            >
                <SeismicMiniMapContent mapSrid={miniMapSrid}  opened={opened} hasGeometryError={hasGeometryError} dataGeometryLine={dataGeometryLine} allLayers={allLayers.current} visibleLayers={visibleLayers} />
            </GeopostPopup>
            :
            <React.Fragment/>
    );
}

interface Props {
    opened: boolean;
    setOpened: (value: boolean) => void;
    dataGeometryLine: ILineString | null;
    hasGeometryError: boolean;
}